import axios from 'axios';

const TERMINAL = new Set(['completed', 'failed']);

// True when a failed job's error indicates the AI provider is not configured/unavailable.
// We map raw provider errors to a governed flag and never surface the raw string to users.
export function isAiUnavailableError(job) {
  const s = `${job?.status || ''} ${job?.error || ''}`.toLowerCase();
  return (
    (job?.status === 'AI_UNAVAILABLE') ||
    s.includes('ai_unavailable') ||
    s.includes('not configured') ||
    s.includes('api key') ||
    s.includes('no ai provider')
  );
}

/**
 * Poll a set of analysis-job ids until every job reaches a terminal state
 * (completed/failed) or the timeout elapses. Returns AUTHORITATIVE counts from
 * the backend — never infers success from upload/submission alone.
 *
 * @returns {Promise<{completed:number, failed:number, pending:number, aiUnavailable:boolean, failedUploadIds:string[], jobs:object[]}>}
 */
export async function pollJobsToTerminal(apiBase, jobIds, { timeoutMs = 120000, intervalMs = 2500, onTick } = {}) {
  const ids = [...new Set((jobIds || []).filter(Boolean))];
  const start = Date.now();
  let jobs = [];

  const snapshot = async () => {
    const results = await Promise.all(
      ids.map(async (id) => {
        try {
          const r = await axios.get(`${apiBase}/analysis-jobs/${id}`);
          return r.data;
        } catch {
          return { id, status: 'failed', error: 'status unavailable' };
        }
      })
    );
    return results;
  };

  // eslint-disable-next-line no-constant-condition
  while (true) {
    jobs = await snapshot();
    const completed = jobs.filter((j) => j.status === 'completed').length;
    const failed = jobs.filter((j) => j.status === 'failed').length;
    const pending = jobs.length - completed - failed;
    if (typeof onTick === 'function') onTick({ completed, failed, pending, total: jobs.length });
    if (pending === 0 || Date.now() - start > timeoutMs) break;
    // eslint-disable-next-line no-await-in-loop
    await new Promise((res) => setTimeout(res, intervalMs));
  }

  const completed = jobs.filter((j) => j.status === 'completed').length;
  const failedJobs = jobs.filter((j) => j.status !== 'completed');
  const failed = failedJobs.length;
  const aiUnavailable = failed > 0 && failedJobs.every((j) => isAiUnavailableError(j));
  const failedUploadIds = failedJobs.map((j) => j.inputUploadId || j.uploadId).filter(Boolean);

  return { completed, failed, pending: 0, aiUnavailable, failedUploadIds, jobs };
}

// Resolve an overall ingestion state from authoritative counts.
export function resolveIngestionState({ submitted, completed, failed, aiUnavailable }) {
  if (aiUnavailable && completed === 0) return 'AI_UNAVAILABLE';
  if (completed === 0 && failed > 0) return 'FAILED';
  if (failed > 0 && completed > 0) return 'PARTIALLY_COMPLETED';
  if (completed === submitted && submitted > 0) return 'COMPLETED';
  if (submitted === 0) return 'FAILED';
  return 'PARTIALLY_COMPLETED';
}
